/* eslint-disable no-param-reassign */
import React, { useRef, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { MenuItem } from "react-bootstrap";
import * as R from "ramda";

import {
  ActionBar,
  notifierShape,
  apiShape,
  withNotifier,
  withApi,
  ActionDropdownButton,
  intlShape,
  ActionBarButtons,
  withConfiguration,
  withFlipper,
  SearchBar,
  Loader,
} from "lcm-iot-commons";
import isElementHiddenViaFlipper from "lcm-iot-commons/client/lib/utils/flipperUtils";

import ServiceSubscriptionItem from "./ServiceSubscriptionItem";
import { extractServiceSubscriptions } from "../../extractors/serviceSubscriptionsExtractor";

type Props = {
  resellerView: boolean;
  api: apiShape;
  flipper: {
    oneSubscription?: boolean;
    hideServiceSubscriptionsCreateAction?: boolean;
  };
  notifier: notifierShape;
  intl: intlShape;
  configuration: {
    applications?: $TSFixMe;
    hiddenApplications?: string[];
    netilionUrl?: string;
  };
};

export function ServiceSubscriptions({
  resellerView,
  api,
  notifier,
  intl,
  configuration,
  flipper,
}: Props) {
  const [fetching, setFetching] = useState(true);
  const [subscriptions, setSubscriptions] = useState<$TSFixMe[] | undefined>();
  const [totalCount, setTotalCount] = useState();
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<
    $TSFixMe[] | undefined
  >(undefined);
  const { applications, hiddenApplications, netilionUrl } = configuration;
  const [filters, setFilters] = useState({ renewalTime: "none" });

  const byStatusAndName = R.sortWith([
    R.ascend(R.propEq("cancelled", "status")),
    // @ts-expect-error TS(2345) FIXME: Argument of type '<U extends { applicationName?: u... Remove this comment to see the full error message
    R.ascend(R.prop("applicationName")),
  ]);
  const subsRef = useRef(subscriptions);
  subsRef.current = subscriptions;
  const intervalRef = useRef();

  const addAssetsToSubscriptions = (subscriptionsParam: $TSFixMe) =>
    subscriptionsParam.map(async (subscription: $TSFixMe) => subscription);

  const loadData = async () => {
    try {
      setFetching(true);
      let subscriptionsCollection = [];

      if (resellerView) {
        // this view should only contain subscriptions that the reseller sold to others (in other words 'manage')
        subscriptionsCollection = extractServiceSubscriptions(
          await api.getAll("/subscriptions", { scope: "RESELLER" }),
        );

        subscriptionsCollection.forEach((subscription: $TSFixMe) => {
          subscription.pricingModel =
            subscription.externalReference && subscription.status !== "open"
              ? "paid"
              : "free";
          subscription.usable = false;
        });
      } else {
        // this view should contain subscriptions he can use (own + seats) but also readable ones through permissions
        subscriptionsCollection = extractServiceSubscriptions(
          await api.getAll("/subscriptions", {
            scope: "USER",
            status: "confirmed,payment_required,non_renewing,in_trial",
          }),
        );
        subscriptionsCollection.forEach((subscription: $TSFixMe) => {
          subscription.pricingModel =
            subscription.externalReference && subscription.status !== "open"
              ? "paid"
              : "free";
        });
      }

      subscriptionsCollection = addAssetsToSubscriptions(
        subscriptionsCollection,
      );
      await Promise.all(subscriptionsCollection).then((subs) => {
        setSubscriptions(subs);
        setTotalCount(subscriptionsCollection.length);
      });
    } catch (errors) {
      // istanbul ignore next
      notifier.showError(api.translateError(errors));
    } finally {
      setFetching(false);
    }
  };

  React.useEffect(() => {
    loadData();

    return () => {
      clearInterval(intervalRef?.current);
    };
  }, []);

  const loader = <Loader id="subscriptions-list-loader" loading={fetching} />;

  const badge = fetching ? (
    <span className="loading">
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </span>
  ) : (
    totalCount
  );

  const renderBadge =
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    totalCount > 0 ? (
      <span id="subscriptions-count" className="badge">
        {badge}
      </span>
    ) : null;

  const noSubscriptionsFound =
    subscriptions && !fetching && (subscriptions as $TSFixMe).length === 0 ? (
      <div id="no-subscription-found" className="clue">
        <div className="clue-header">
          <FormattedMessage id="subscription.no_service_found" />
        </div>
        <div className="clue-details">
          <FormattedMessage
            id={`subscription.no_service_found.${resellerView ? "reseller_" : ""}details`}
          />
        </div>
      </div>
    ) : null;

  const subscriptionsListItems =
    subscriptions && !fetching ? (
      <ul id="subscription-list" className="list">
        {byStatusAndName(filteredSubscriptions || subscriptions).map(
          (subscription) => (
            <ServiceSubscriptionItem
              // @ts-expect-error TS(2322) FIXME: Type '{ subscription: Record<"status", string>; ke... Remove this comment to see the full error message
              subscription={subscription}
              key={(subscription as $TSFixMe).id}
              usable={(subscription as $TSFixMe).usable}
              resellerView={resellerView}
            />
          ),
        )}
      </ul>
    ) : null;

  const extendedApplications = applications;
  (extendedApplications as $TSFixMe).netilion = netilionUrl;

  const excludedList = flipper?.oneSubscription
    ? [
        "id",
        "analytics",
        "health",
        "library",
        "value",
        "liquiline_assist",
        "process",
      ]
    : ["id", "netilion", "liquiline_assist"];

  const applicationItems = Object.keys(extendedApplications)
    .sort()
    .filter((key) => !excludedList.includes(key))
    .filter((key) => !hiddenApplications?.includes(key))
    .map((key) => {
      const enabled = resellerView
        ? true
        : !(subscriptions as $TSFixMe)?.find(
            (sub: $TSFixMe) =>
              sub.applicationName.replace(/\s/g, "").toLowerCase() === key,
          ) &&
          (!flipper?.oneSubscription ||
            ![
              "analytics",
              "health",
              "library",
              "value",
              "liquiline_assist",
            ].includes(key));

      const url = resellerView
        ? `${extendedApplications[key]}/reseller/subscriptions/create`
        : extendedApplications[key];

      return (
        <MenuItem
          id={`menu-item-${key}`}
          key={key}
          className="menu-item"
          href={url}
          disabled={!enabled}
        >
          <span>{intl.formatMessage({ id: `app.${key}` })}</span>
        </MenuItem>
      );
    });

  const title = resellerView ? (
    <FormattedMessage id="label.reseller_service_subscriptions" />
  ) : (
    <FormattedMessage id="label.service_subscriptions" />
  );

  const t = (key: $TSFixMe) => intl.formatMessage({ id: key }).toLowerCase();

  const translation = {
    [t`subscription_details.cancelled`]: "cancelled",
    [t`subscription_details.active`]: "confirmed",
    [t`subscription_details.in_trial`]: "in_trial",
    [t`subscription_details.non_renewing`]: "non_renewing",
    [t`subscription_details.open`]: "open",
    [t`subscription_details.payment_required`]: "payment_required",
  };

  // This function needs to be idempotent
  const onSearch = (searchedValue: $TSFixMe) => {
    // eslint-disable-next-line no-shadow
    const subscriptions = subsRef.current;
    if (!subscriptions && !intervalRef.current) {
      // @ts-expect-error TS(2322) FIXME: Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
      intervalRef.current = setInterval(() => onSearch(searchedValue), 1000);
    }
    if (!subscriptions) return null;
    if (!searchedValue) {
      setTotalCount((subscriptions as $TSFixMe).length);
      // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      return setFilteredSubscriptions(null);
    }
    const query = searchedValue.toLowerCase();
    const match = Object.entries(translation)
      .find((trans) => trans[0].includes(query))
      ?.pop();

    const filteredSubs = match
      ? (subscriptions as $TSFixMe).filter(
          (sub: $TSFixMe) => sub.status === match,
        )
      : (subscriptions as $TSFixMe).filter((sub: $TSFixMe) =>
          Object.values(sub)
            .map((e) => String(e).toLocaleLowerCase())
            .find((e) => e.includes(query)),
        );

    setFilteredSubscriptions(filteredSubs);
    setTotalCount(filteredSubs.length);
    return filteredSubs;
  };

  const renderCreateActionButton = !isElementHiddenViaFlipper(
    flipper,
    "hideServiceSubscriptionsCreateAction",
  ) ? (
    <ActionDropdownButton
      // @ts-expect-error TS(2322)
      id="create-button"
      icon="icon-eh-new"
      title={intl.formatMessage({ id: "button.create" })}
    >
      {applicationItems}
    </ActionDropdownButton>
  ) : null;

  const RENEWAL_TIME_OPTIONS = [
    {
      value: "none",
      labelId: "label.reseller_subscriptions_filter",
    },
    {
      value: "all",
      labelId: "label.reseller_subscriptions_all",
    },
    {
      value: "30",
      labelId: "label.reseller_subscription_renewal_time_30",
    },
    {
      value: "60",
      labelId: "label.reseller_subscription_renewal_time_60",
    },
    {
      value: "90",
      labelId: "label.reseller_subscription_renewal_time_90",
    },
  ];

  const getRenewalTimeLabel = () => {
    const current = RENEWAL_TIME_OPTIONS.find(
      (o) => o.value === filters.renewalTime,
    )!;

    return intl.formatMessage({ id: current.labelId });
  };

  const filteredByRenewalTime = (value: string) => {
    const threshold = Number.parseInt(value, 10);

    return subscriptions?.filter((sub) => {
      const billedUntil = new Date(sub.billedUntil);
      const today = new Date();
      const start = new Date(today);
      const end = new Date(today);
      end.setDate(today.getDate() + threshold);

      return (
        sub.status === "confirmed" && billedUntil >= start && billedUntil <= end
      );
    });
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col-xs-12">
          <ActionBar>
            <h1 id="subscriptions-header">
              {title}
              {renderBadge}
            </h1>
            <ActionBarButtons>{renderCreateActionButton}</ActionBarButtons>
          </ActionBar>
          <SearchBar
            // @ts-expect-error TS(2322) FIXME: Type '{ onSearch: (searchedValue: any) => any; pla... Remove this comment to see the full error message
            onSearch={onSearch}
            placeholder={intl.formatMessage({
              id: "reseller_customer.subscription.search_placeholder",
            })}
          />
          <div className="filters-dropdown-item">
            <ActionBar>
              <ActionBarButtons>
                <ActionDropdownButton
                  // @ts-expect-error TS(2322)
                  id="filter-button"
                  icon="icon-eh-filter"
                  title={getRenewalTimeLabel()}
                >
                  {RENEWAL_TIME_OPTIONS.filter(
                    (option) => option.value !== "none",
                  ).map((option) => (
                    <MenuItem
                      key={option.value}
                      id={`renewal-time-${option.value}`}
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          renewalTime:
                            option.value === "all" ? "none" : option.value,
                        }));
                        if (option.value !== "all" && option.value !== "none") {
                          setFilteredSubscriptions(
                            filteredByRenewalTime(option.value),
                          );
                        } else {
                          setFilteredSubscriptions(undefined);
                        }
                      }}
                    >
                      {intl.formatMessage({ id: option.labelId })}
                    </MenuItem>
                  ))}
                </ActionDropdownButton>
              </ActionBarButtons>
            </ActionBar>
          </div>
        </div>
      </div>
      {loader}
      {noSubscriptionsFound}
      {subscriptionsListItems}
    </div>
  );
}

export default injectIntl(
  withFlipper(withConfiguration(withApi(withNotifier(ServiceSubscriptions)))),
);
